import { Box, Text } from "@chakra-ui/react";
import { GroupBase, MenuListProps, components } from "react-select";
import { formatInteger } from "utils/formatting";
import { isNewOption } from "./utils";

export type PlaceholderOption = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  __isPlaceholder__: true;
};

export function isPlaceholderOption(
  option: unknown,
): option is PlaceholderOption {
  return (
    typeof option === "object" &&
    option !== null &&
    "__isPlaceholder__" in option &&
    // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle
    (option as PlaceholderOption).__isPlaceholder__ === true
  );
}

export default function MenuListWithResultCount<
  Option extends object,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({ children, options, ...props }: MenuListProps<Option, IsMulti, Group>) {
  const withoutNew = options.filter((o) => !isNewOption(o));

  // Count actual rendered children (exclude placeholder objects)
  const displayedCount = withoutNew.filter(
    (o) => !isPlaceholderOption(o),
  ).length;

  // Get total from the array length
  const totalCount = withoutNew?.length;

  // Show indicator if we have more options than shown (length > actual elements)
  const hasMore =
    !!totalCount && displayedCount > 0 && totalCount > displayedCount;

  return (
    <components.MenuList {...props} options={options}>
      {children}
      {hasMore && (
        <Box
          px={3}
          py={2}
          mb="-4px"
          fontSize=".85em"
          color="gray.500"
          borderTop="1px solid"
          borderColor="gray.200"
          borderBottomRadius="4px"
          bg="gray.50"
          lineHeight="normal"
          textAlign="center"
        >
          <Text>
            Showing {formatInteger(displayedCount)} of{" "}
            {formatInteger(totalCount)} results.
          </Text>
          <Text fontSize=".85em">
            Refine your search to see additional results.
          </Text>
        </Box>
      )}
    </components.MenuList>
  );
}
